import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getTours, createBooking, createCheckout } from "@/api";
import { useLang, localized } from "@/i18n";
import { waLink } from "@/config";
import { trackBookingCompleted, trackWhatsAppClick } from "@/analytics";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";

const emptyForm = {
  tour_id: "",
  date: "",
  schedule: "",
  adults: 2,
  children: 0,
  name: "",
  email: "",
  whatsapp: "",
  comments: "",
  extras: [],
};

const EXTRA_PRICE_MXN = 1500;
const EXTRAS_BUNDLE_MXN = 2500;

export default function BookingPage() {
  const { t, lang } = useLang();
  const { data: tours = [] } = useQuery({ queryKey: ["tours"], queryFn: getTours });
  const [params] = useSearchParams();
  const nav = useNavigate();
  const preslug = params.get("tour");

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    date: params.get("date") || "",
    adults: Number(params.get("pax")) > 0 ? Number(params.get("pax")) : 2,
  }));
  const [done, setDone] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [paying, setPaying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (preslug && tours.length && !form.tour_id) {
      const found = tours.find((t) => t.slug === preslug);
      if (found) setForm((f) => ({ ...f, tour_id: found.id }));
    }
  }, [preslug, tours, form.tour_id]);

  const selectedTour = tours.find((t) => t.id === form.tour_id);
  const isBalloon = !!selectedTour && selectedTour.price_mxn > 0; // extras apply to all packages
  const extras = isBalloon ? form.extras : [];
  const extrasTotal = extras.length >= 2 ? EXTRAS_BUNDLE_MXN : extras.length * EXTRA_PRICE_MXN;
  const total = selectedTour
    ? selectedTour.price_mxn * (Number(form.adults) + Number(form.children)) + extrasTotal
    : 0;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleExtra = (key) =>
    setForm((f) => ({
      ...f,
      extras: f.extras.includes(key) ? f.extras.filter((e) => e !== key) : [...f.extras, key],
    }));

  const extraLabel = (key) => (key === "drone_video" ? t("booking.extra_drone") : t("booking.extra_photos"));

  const validStep = () => {
    if (step === 1) return !!form.tour_id && !!form.date;
    if (step === 2) return form.adults >= 1;
    if (step === 3) return form.name && form.email && form.whatsapp;
    return true;
  };

  const next = () => {
    if (!validStep()) {
      toast.error(lang === "es" ? "Completa los campos requeridos" : "Please complete required fields");
      return;
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const booking = await createBooking({
        tour_id: form.tour_id,
        date: form.date,
        schedule: form.schedule || null,
        adults: Number(form.adults),
        children: Number(form.children),
        name: form.name,
        email: form.email,
        whatsapp: form.whatsapp,
        comments: form.comments,
        extras,
      });
      trackBookingCompleted({ tourName: localized(selectedTour.name, lang), value: total });
      setCreatedBooking(booking);
      setDone(true);
    } catch (e) {
      toast.error(lang === "es" ? "Ocurrió un error, intenta de nuevo." : "Something went wrong, try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const waConfirm = () => {
    if (!selectedTour) return;
    let msg = `Hola, me interesa reservar el tour "${localized(selectedTour.name, lang)}". Me gustaría conocer disponibilidad para ${form.date || "[fecha]"}. Somos ${form.adults} adultos y ${form.children} niños.`;
    if (extras.length) {
      msg += ` Me interesan los extras: ${extras.map(extraLabel).join(" + ")} (MX$${extrasTotal.toLocaleString()}).`;
    }
    trackWhatsAppClick("booking-wizard");
    window.open(waLink(msg), "_blank");
  };

  const payNow = async () => {
    if (!createdBooking) return;
    setPaying(true);
    try {
      const { checkout_url } = await createCheckout(createdBooking.id);
      window.location.href = checkout_url;
    } catch {
      toast.error(lang === "es" ? "No se pudo iniciar el pago. Intenta de nuevo." : "Could not start payment. Try again.");
      setPaying(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="pt-40 pb-32 max-w-2xl mx-auto px-6 text-center">
        <CheckCircle2 size={56} className="mx-auto text-[#C4A661]" strokeWidth={1} />
        <h1 className="font-serif-hc text-5xl mt-8 text-[#F5F2EA]">{t("booking.success_title")}</h1>
        <p className="mt-5 text-[#A3A095] leading-relaxed">{t("booking.success_msg")}</p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          {createdBooking && selectedTour?.price_mxn > 0 && (
            <button data-testid="booking-pay-now" onClick={payNow} disabled={paying} className="hc-btn-primary disabled:opacity-60">
              {paying ? "…" : `${t("booking.pay_now")} · MX$${(createdBooking.total_price_mxn || total).toLocaleString()}`}
            </button>
          )}
          <button onClick={waConfirm} className="hc-btn-wa">{t("booking.wa_confirm")}</button>
          <button onClick={() => nav("/")} className="hc-btn-secondary">{lang === "es" ? "Volver al inicio" : "Back to home"}</button>
        </div>
        {createdBooking && selectedTour?.price_mxn > 0 && (
          <p className="mt-5 text-xs text-[#A3A095]">{t("booking.pay_hint")}</p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-3xl mx-auto px-6">
        <p className="eyebrow mb-4">{t("booking.step")} {step} {t("booking.of")} 4</p>
        <h1 className="font-serif-hc font-light text-4xl md:text-6xl leading-[1.02] text-[#F5F2EA]">
          {t("booking.title")}
        </h1>

        <div className="mt-10 flex gap-1">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-[2px] flex-1 ${s <= step ? "bg-[#C4A661]" : "bg-[#1E2B25]"}`} />
          ))}
        </div>

        <div className="mt-12 bg-[#0C1310] border border-[#1E2B25] p-8 md:p-10">
          {step === 1 && (
            <div className="space-y-6">
              <label className="block">
                <span className="eyebrow block mb-2">{t("booking.select_tour")}</span>
                <select
                  data-testid="booking-tour-select"
                  value={form.tour_id}
                  onChange={(e) => set("tour_id", e.target.value)}
                  className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-4 text-[#F5F2EA]"
                >
                  <option value="" className="bg-[#0C1310]">—</option>
                  {tours.map((tt) => (
                    <option key={tt.id} value={tt.id} className="bg-[#0C1310]">{localized(tt.name, lang)}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="eyebrow block mb-2">{t("booking.date")}</span>
                <input
                  data-testid="booking-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => set("date", e.target.value)}
                  className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-4 text-[#F5F2EA]"
                />
              </label>
              {selectedTour?.schedules?.length > 0 && (
                <label className="block">
                  <span className="eyebrow block mb-2">{t("booking.schedule")}</span>
                  <select
                    data-testid="booking-schedule"
                    value={form.schedule}
                    onChange={(e) => set("schedule", e.target.value)}
                    className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-4 text-[#F5F2EA]"
                  >
                    <option value="" className="bg-[#0C1310]">—</option>
                    {selectedTour.schedules.map((s) => <option key={s} value={s} className="bg-[#0C1310]">{s}</option>)}
                  </select>
                </label>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <label className="block">
                <span className="eyebrow block mb-2">{t("booking.adults")}</span>
                <input data-testid="booking-adults" type="number" min="1" value={form.adults} onChange={(e) => set("adults", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-4 text-[#F5F2EA]" />
              </label>
              <label className="block">
                <span className="eyebrow block mb-2">{t("booking.children")}</span>
                <input data-testid="booking-children" type="number" min="0" value={form.children} onChange={(e) => set("children", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-4 text-[#F5F2EA]" />
              </label>
              {isBalloon && (
                <div data-testid="booking-extras" className="pt-2">
                  <span className="eyebrow block mb-1">{t("booking.extras_title")}</span>
                  <p className="text-sm text-[#A3A095] mb-4">{t("booking.extras_hint")}</p>
                  <div className="space-y-3">
                    {[
                      { key: "drone_video", testid: "booking-extra-drone-video" },
                      { key: "photo_package", testid: "booking-extra-photo-package" },
                    ].map(({ key, testid }) => {
                      const checked = form.extras.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={testid}
                          onClick={() => toggleExtra(key)}
                          className={`w-full flex items-center justify-between border p-4 text-left transition-colors ${checked ? "border-[#C4A661] bg-[#C4A661]/5" : "border-[#1E2B25] hover:border-[#3A4A42]"}`}
                        >
                          <span className="flex items-center gap-3">
                            <span className={`w-4 h-4 border flex items-center justify-center ${checked ? "border-[#C4A661] bg-[#C4A661]" : "border-[#3A4A42]"}`}>
                              {checked && <CheckCircle2 size={12} className="text-[#0C1310]" />}
                            </span>
                            <span className="text-[#F5F2EA]">{extraLabel(key)}</span>
                          </span>
                          <span className="text-[#C4A661]">MX$1,500</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className={`mt-3 text-sm ${extras.length >= 2 ? "text-[#C4A661]" : "text-[#A3A095]"}`} data-testid="booking-extras-bundle-note">
                    {extras.length >= 2 ? `✓ ${t("booking.extras_bundle_applied")} — MX$2,500` : t("booking.extras_bundle_note")}
                  </p>
                </div>
              )}
              {selectedTour && selectedTour.price_mxn > 0 && (
                <div className="pt-4 border-t border-[#1E2B25] space-y-2">
                  {extrasTotal > 0 && (
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-[#A3A095]">{t("booking.extras_label")}</span>
                      <span className="text-[#F5F2EA]" data-testid="booking-extras-subtotal">MX${extrasTotal.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline">
                    <span className="eyebrow">{t("booking.total")}</span>
                    <span className="font-serif-hc text-3xl text-[#C4A661]" data-testid="booking-total">MX${total.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <label className="block">
                <span className="eyebrow block mb-2">{t("booking.name")}</span>
                <input data-testid="booking-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-4 text-[#F5F2EA]" />
              </label>
              <label className="block">
                <span className="eyebrow block mb-2">{t("booking.email")}</span>
                <input data-testid="booking-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-4 text-[#F5F2EA]" />
              </label>
              <label className="block">
                <span className="eyebrow block mb-2">{t("booking.whatsapp")}</span>
                <input data-testid="booking-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+52 ..." className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-4 text-[#F5F2EA]" />
              </label>
              <label className="block">
                <span className="eyebrow block mb-2">{t("booking.comments")}</span>
                <textarea data-testid="booking-comments" rows="4" value={form.comments} onChange={(e) => set("comments", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-4 text-[#F5F2EA] resize-none" />
              </label>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <p className="eyebrow">{t("booking.summary")}</p>
              <div className="space-y-3 text-[15px]">
                <div className="flex justify-between border-b border-[#1E2B25] pb-2"><span className="text-[#A3A095]">{t("booking.select_tour")}</span><span className="text-[#F5F2EA]">{selectedTour ? localized(selectedTour.name, lang) : "—"}</span></div>
                <div className="flex justify-between border-b border-[#1E2B25] pb-2"><span className="text-[#A3A095]">{t("booking.date")}</span><span className="text-[#F5F2EA]">{form.date || "—"}</span></div>
                {form.schedule && <div className="flex justify-between border-b border-[#1E2B25] pb-2"><span className="text-[#A3A095]">{t("booking.schedule")}</span><span className="text-[#F5F2EA]">{form.schedule}</span></div>}
                <div className="flex justify-between border-b border-[#1E2B25] pb-2"><span className="text-[#A3A095]">{t("booking.adults")} / {t("booking.children")}</span><span className="text-[#F5F2EA]">{form.adults} / {form.children}</span></div>
                <div className="flex justify-between border-b border-[#1E2B25] pb-2"><span className="text-[#A3A095]">{t("booking.name")}</span><span className="text-[#F5F2EA]">{form.name}</span></div>
                <div className="flex justify-between border-b border-[#1E2B25] pb-2"><span className="text-[#A3A095]">{t("booking.email")}</span><span className="text-[#F5F2EA]">{form.email}</span></div>
                <div className="flex justify-between border-b border-[#1E2B25] pb-2"><span className="text-[#A3A095]">{t("booking.whatsapp")}</span><span className="text-[#F5F2EA]">{form.whatsapp}</span></div>
                {extras.length > 0 && (
                  <div className="flex justify-between border-b border-[#1E2B25] pb-2" data-testid="booking-summary-extras">
                    <span className="text-[#A3A095]">{t("booking.extras_label")}</span>
                    <span className="text-[#F5F2EA]">{extras.map(extraLabel).join(" + ")} · MX${extrasTotal.toLocaleString()}</span>
                  </div>
                )}
                {total > 0 && (
                  <div className="flex justify-between pt-4"><span className="eyebrow">{t("booking.total")}</span><span className="font-serif-hc text-3xl text-[#C4A661]">MX${total.toLocaleString()}</span></div>
                )}
              </div>
            </div>
          )}

          <div className="mt-10 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <button data-testid="booking-back" onClick={back} disabled={step === 1} className="text-[11px] tracking-[0.22em] uppercase text-[#A3A095] hover:text-[#F5F2EA] disabled:opacity-30 inline-flex items-center gap-2">
              <ArrowLeft size={14} /> {t("booking.back")}
            </button>
            {step < 4 ? (
              <button data-testid="booking-next" onClick={next} className="hc-btn-primary">
                {t("booking.next")} <ArrowRight size={14} className="ml-2" />
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <button data-testid="booking-wa" onClick={waConfirm} className="hc-btn-wa">{t("booking.wa_confirm")}</button>
                <button data-testid="booking-confirm" onClick={submit} disabled={submitting} className="hc-btn-primary">
                  {submitting ? "…" : t("booking.confirm")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
